/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ReactElement } from 'react';
import { useRouteError } from 'react-router';
import { Link } from '@/components/link';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';

/**
 * In-page fallback rendered by a route's `ErrorBoundary` export.
 *
 * A route-level boundary replaces only the layout's `<Outlet/>`, so the storefront
 * chrome (header, footer, brand theme) stays on screen — unlike the root boundary in
 * `src/root.tsx`, which swaps the entire document for an unbranded error page.
 *
 * This is the same shape the wishlist and order routes already use
 * (`WishlistLoadError`, `OrderNotFoundCard`): `role="alert"`, honest brand-neutral copy,
 * a way onward, and DEV-only `NormalizedApiError` detail. It is parameterised so each
 * demo-path route supplies its own copy and destinations rather than each route
 * re-implementing the same markup.
 *
 * `reloadDocument` on the primary link forces a full document load so the retry cannot
 * re-use the in-memory rejected loader promise that put us here.
 *
 * @param title - Optional headline. Omit for routes whose established copy is message-only (cart).
 * @param message - Honest, brand-neutral description of what failed.
 * @param retryHref - Destination for the primary "try again" link (site prefix applied by `Link`).
 * @param retryLabel - Label for the primary link.
 * @param secondaryHref - Optional destination for a way onward (e.g. back to cart).
 * @param secondaryLabel - Label for the secondary link; required for it to render.
 */
export function RouteLoadError({
    title,
    message,
    retryHref,
    retryLabel,
    secondaryHref,
    secondaryLabel,
}: {
    title?: string;
    message: string;
    retryHref: string;
    retryLabel: string;
    secondaryHref?: string;
    secondaryLabel?: string;
}): ReactElement {
    const rawError = useRouteError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;

    return (
        <div role="alert" className="section-container py-16 text-center">
            {title && <p className="type-body text-foreground font-medium">{title}</p>}
            <p className="text-base text-muted-foreground">{message}</p>
            <div className="mt-4 flex flex-wrap items-center justify-center gap-6">
                <Link to={retryHref} reloadDocument className="inline-block text-primary underline">
                    {retryLabel}
                </Link>
                {secondaryHref && secondaryLabel && (
                    <Link to={secondaryHref} className="inline-block text-primary underline">
                        {secondaryLabel}
                    </Link>
                )}
            </div>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}
